<template>
  <el-menu
    ref="menuRef"
    :default-active="activeMenu"
    :default-openeds="defaultOpeneds"
    class="side-menu"
    :collapse="isCollapse"
  >
    <template v-for="route in menuRoutes" :key="route.path">
      <!-- Leaf Node (No Children) -->
      <el-menu-item 
        v-if="!route.children || route.children.length === 0" 
        :index="resolvePath(route.path)"
        class="level-1-leaf"
        @click="handleMenuItemClick(resolvePath(route.path))"
      >
        <div class="submenu-title-clickable">
          <el-icon v-if="route.meta?.icon"><component :is="route.meta.icon" /></el-icon>
          <span>{{ getTitle(route) }}</span>
        </div>
      </el-menu-item>

      <!-- Sub Menu (Has Children) -->
      <el-sub-menu 
        v-else 
        :index="resolvePath(route.path)"
        popper-class="side-menu-popper"
        :class="{ 'is-active-parent': isActiveParent(route) }"
      >
        <template #title>
          <div class="submenu-title-clickable" @click.stop="handleSubMenuTitleClick(route)">
            <el-icon v-if="route.meta?.icon"><component :is="route.meta.icon" /></el-icon>
            <span>{{ getTitle(route) }}</span>
          </div>
        </template>
        
        <template v-for="child in route.children" :key="child.path">
           <!-- Level 2 Leaf -->
           <el-menu-item 
             v-if="!child.children || child.children.length === 0"
             :index="resolvePath(route.path, child.path)"
             @click="handleMenuItemClick(resolvePath(route.path, child.path))"
           >
             <span>{{ getTitle(child) }}</span>
           </el-menu-item>

           <!-- Level 2 Sub Menu -->
           <el-sub-menu 
             v-else 
             :index="resolvePath(route.path, child.path)"
             :class="{ 'is-active-parent': isActiveParent(child, route.path) }"
           >
             <template #title>
               <div class="submenu-title-clickable" @click.stop="handleSubMenuTitleClick(child, route.path)">
                 <span>{{ getTitle(child) }}</span>
               </div>
             </template>
             <!-- Level 3 Leaf -->
             <el-menu-item 
               v-for="grandchild in child.children" 
               :key="grandchild.path"
               :index="resolvePath(route.path, child.path, grandchild.path)"
               @click="handleMenuItemClick(resolvePath(route.path, child.path, grandchild.path))"
             >
               <span>{{ getTitle(grandchild) }}</span>
             </el-menu-item>
           </el-sub-menu>
        </template>
      </el-sub-menu>
    </template>
  </el-menu>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useRouter, useRoute, type RouteRecordRaw } from 'vue-router'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
  isCollapse: boolean
}>()

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const menuRef = ref()

const menuRoutes = computed(() => {
  const mainRoute = router.options.routes.find(r => r.path === '/')
  return mainRoute?.children || []
})

const activeMenu = computed(() => route.path)

// 计算当前路由需要展开的所有父级菜单
const defaultOpeneds = computed(() => {
  const openeds: string[] = []
  const currentPath = route.path
  
  // 遍历所有路由找到当前路径的所有父级
  for (const menuRoute of menuRoutes.value) {
    const menuPath = resolvePath(menuRoute.path)
    
    if (currentPath.startsWith(menuPath) && menuRoute.children?.length) {
      openeds.push(menuPath)
      
      // 检查二级菜单
      for (const child of menuRoute.children) {
        const childPath = resolvePath(menuRoute.path, child.path)
        if (currentPath.startsWith(childPath) && child.children?.length) {
          openeds.push(childPath)
        }
      }
    }
  }
  
  return openeds
})

// 检查菜单项是否是当前活动路由的父级
function isActiveParent(menuRoute: RouteRecordRaw, parentPath: string = ''): boolean {
  const currentPath = route.path
  const menuPath = parentPath 
    ? resolvePath(parentPath, menuRoute.path)
    : resolvePath(menuRoute.path)
  
  return currentPath.startsWith(menuPath) && currentPath !== menuPath
}

function getTitle(item: RouteRecordRaw): string {
  const titleKey = item.meta?.titleKey as string
  if (titleKey) {
    return t(titleKey)
  }
  return (item.meta?.title as string) || item.name?.toString() || ''
}

function resolvePath(...paths: string[]) {
  return '/' + paths.filter(p => p).join('/').replace(/^\/+/, '')
}

// 处理菜单项点击 - 直接导航
function handleMenuItemClick(path: string) {
  router.push(path)
}

// 处理子菜单标题点击 - 导航到第一个叶子节点，同时确保菜单保持展开
function handleSubMenuTitleClick(item: RouteRecordRaw, parentPath: string = '') {
  if (props.isCollapse) return
  
  // 找到第一个叶子节点
  const firstLeaf = findFirstLeaf(item, parentPath)
  if (firstLeaf) {
    // 先展开菜单（如果还没展开）
    const menuPath = parentPath 
      ? resolvePath(parentPath, item.path)
      : resolvePath(item.path)
    
    // 确保菜单展开
    if (menuRef.value) {
      menuRef.value.open(menuPath)
    }
    
    // 然后导航
    router.push(firstLeaf)
  }
}

// 递归找到第一个叶子节点路径
function findFirstLeaf(item: RouteRecordRaw, parentPath: string = ''): string | null {
  const currentPath = parentPath 
    ? resolvePath(parentPath, item.path)
    : resolvePath(item.path)
  
  if (!item.children || item.children.length === 0) {
    return currentPath
  }
  
  // 有子节点，递归找第一个子节点的叶子
  const firstChild = item.children[0]
  if (!firstChild) return currentPath
  return findFirstLeaf(firstChild, currentPath)
}

// 当路由变化时，确保对应的父级菜单展开
watch(() => route.path, async () => {
  await nextTick()
  if (menuRef.value) {
    // 展开当前路由的所有父级菜单
    for (const path of defaultOpeneds.value) {
      menuRef.value.open(path)
    }
  }
}, { immediate: true })

onMounted(async () => {
  await nextTick()
  // 初始化时展开当前路由的父级菜单
  if (menuRef.value) {
    for (const path of defaultOpeneds.value) {
      menuRef.value.open(path)
    }
  }
})
</script>

<style scoped lang="scss">
.side-menu {
  border-right: none;
  flex: 1;
  overflow-y: auto;
  
  // 移除默认的router行为，我们手动处理导航
  :deep(.el-menu-item),
  :deep(.el-sub-menu__title) {
    transition: background-color 0.2s, color 0.2s;
  }
}

.side-menu:not(.el-menu--collapse) {
  width: 100%;
}

.side-menu:not(.el-menu--collapse) :deep(.el-sub-menu .el-menu-item) {
  padding-left: calc(var(--el-menu-base-level-padding, 20px) + 2ch) !important;
}

.submenu-title-clickable {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
}

// 一级菜单标题字体样式
.side-menu :deep(.el-sub-menu__title .submenu-title-clickable) {
  font-size: var(--app-menu-level1-font-size, 18px) !important;
  font-weight: var(--el-menu-item-font-weight, 600) !important;
  line-height: var(--el-menu-item-height, 48px) !important;
}

// 一级菜单叶子节点字体样式（例如仪表盘）
.side-menu :deep(.el-menu > .el-menu-item > .submenu-title-clickable) {
  font-size: var(--app-menu-level1-font-size, 18px) !important;
  font-weight: var(--el-menu-item-font-weight, 600) !important;
  line-height: var(--el-menu-item-height, 48px) !important;
}

.side-menu :deep(.el-menu > .el-menu-item > .submenu-title-clickable span) {
  font-size: var(--app-menu-level1-font-size, 18px) !important;
  font-weight: var(--el-menu-item-font-weight, 600) !important;
}

// 仅针对一级叶子节点，避免过多全局覆盖
.side-menu :deep(.level-1-leaf .submenu-title-clickable) {
  font-size: var(--app-menu-level1-font-size, 18px) !important;
  font-weight: var(--el-menu-item-font-weight, 600) !important;
  line-height: var(--el-menu-item-height, 48px) !important;
}

// 二级及叶子菜单字体样式
.side-menu :deep(.el-menu-item .submenu-title-clickable) {
  font-size: var(--app-menu-level2-font-size, 16px);
  font-weight: var(--el-menu-item-font-weight, 500);
  line-height: var(--el-menu-item-height, 48px);
}

// 高亮展开的父级菜单
.is-active-parent {
  > :deep(.el-sub-menu__title) {
    color: var(--el-menu-active-color) !important;
    
    .el-icon {
      color: var(--el-menu-active-color);
    }
  }
}

// 暗色模式下的高亮效果
:root.dark .is-active-parent {
  > :deep(.el-sub-menu__title) {
    background-color: rgba(0, 212, 255, 0.1);
  }
}

// 亮色模式下的高亮效果
:root:not(.dark) .is-active-parent {
  > :deep(.el-sub-menu__title) {
    background-color: var(--el-color-primary-light-9);
  }
}
</style>
